import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

export function PrivacyPolicyEN() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-100 to-indigo-100">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <button
          onClick={() => navigate('/')}
          className="inline-flex items-center text-indigo-600 hover:text-indigo-700 mb-6 transition-colors duration-200"
        >
          <ArrowLeft className="mr-2 h-5 w-5" />
          Back to App
        </button>

        <article className="bg-white rounded-lg shadow-xl p-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-8">Privacy Policy</h1>
          
          <div className="space-y-6 text-gray-700">
            <p className="text-sm text-gray-500">
              Last updated: {new Date().toLocaleDateString()}
            </p>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">1. Data Controller</h2>
              <div className="space-y-3 bg-gray-50 p-4 rounded-lg">
                <p className="leading-relaxed">
                  <strong>The data controller is:</strong>
                </p>
                <div className="ml-4 space-y-1">
                  <p><strong>Name:</strong> Puretech s.r.o.</p>
                  <p><strong>Registration Number:</strong> 05503795</p>
                  <p><strong>Location:</strong> Czech Republic</p>
                  <p><strong>Contact:</strong> support@simplestickers.app</p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">2. Information We Collect</h2>
              <div className="space-y-3">
                <p className="leading-relaxed">
                  <strong>Account Information:</strong> When you create an account, we collect your email address and any profile information you choose to provide.
                </p>
                <p className="leading-relaxed">
                  <strong>Usage Data:</strong> We collect information about how you use our service, including tasks created, projects managed, and feature usage patterns.
                </p>
                <p className="leading-relaxed">
                  <strong>Payment Information:</strong> Payment processing is handled by Polar. We do not store your payment card details on our servers.
                </p>
                <p className="leading-relaxed">
                  <strong>Technical Data:</strong> We automatically collect certain technical information, including IP address, browser type, operating system, and device information.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">3. How We Use Your Information</h2>
              <div className="space-y-2">
                <p className="leading-relaxed">We use the information we collect to:</p>
                <ul className="list-disc ml-6 space-y-1">
                  <li>Provide, maintain, and improve our services</li>
                  <li>Process transactions and manage subscriptions</li>
                  <li>Send you technical notices and support messages</li>
                  <li>Respond to your comments and questions</li>
                  <li>Analyze usage patterns to improve user experience</li>
                  <li>Enforce our terms and conditions</li>
                  <li>Comply with legal obligations</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">4. Information Sharing</h2>
              <div className="space-y-3">
                <p className="leading-relaxed">
                  We do not sell, trade, or otherwise transfer your personal information to third parties except as described in this policy:
                </p>
                <ul className="list-disc ml-6 space-y-1">
                  <li><strong>Service Providers:</strong> We may share information with trusted third parties who assist us in operating our service (e.g., Polar for payment processing, Supabase for database services)</li>
                  <li><strong>Legal Requirements:</strong> We may disclose information when required by law or to protect our rights</li>
                  <li><strong>Business Transfers:</strong> In the event of a merger, acquisition, or sale of assets, user information may be transferred</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">5. Data Security</h2>
              <div className="space-y-3">
                <p className="leading-relaxed">
                  We implement appropriate technical and organizational security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the internet is 100% secure.
                </p>
                <p className="leading-relaxed">
                  <strong>Security Breach Response:</strong> In the event of a personal data breach, we will comply with GDPR:
                </p>
                <ul className="list-disc ml-6 space-y-1">
                  <li>Report the incident to the supervisory authority within 72 hours of discovery</li>
                  <li>Notify affected users if the breach poses a high risk to their rights and freedoms</li>
                  <li>Take steps to minimize impact and prevent future incidents</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">6. Data Retention</h2>
              <div className="space-y-3">
                <p className="leading-relaxed">
                  We retain your personal information for as long as necessary to provide our services. Specific retention periods are:
                </p>
                <ul className="list-disc ml-6 space-y-1">
                  <li><strong>Account data:</strong> Duration of service + 30 days after account deletion</li>
                  <li><strong>Task content (sticky notes):</strong> Until deleted by you or account termination</li>
                  <li><strong>Payment records:</strong> 7 years (Czech accounting law requirement)</li>
                  <li><strong>Analytics data:</strong> Up to 26 months</li>
                  <li><strong>Backup data:</strong> May persist up to 90 days after primary deletion</li>
                  <li><strong>Anonymized data:</strong> Retained indefinitely for statistical purposes</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">7. Your Rights Under GDPR</h2>
              <div className="space-y-2">
                <p className="leading-relaxed">You have the right to:</p>
                <ul className="list-disc ml-6 space-y-1">
                  <li>Access the personal information we hold about you</li>
                  <li>Correct any inaccurate personal information</li>
                  <li>Request deletion of your personal information</li>
                  <li>Object to or restrict processing of your information</li>
                  <li>Data portability (receive a copy of your data in a structured format)</li>
                  <li>Withdraw consent at any time</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">8. Cookies and Tracking</h2>
              <div className="space-y-3">
                <p className="leading-relaxed">
                  We use cookies and similar tracking technologies to enhance your experience. Types of cookies we use:
                </p>
                <ul className="list-disc ml-6 space-y-1">
                  <li><strong>Essential cookies:</strong> Necessary for service functionality (authentication, login)</li>
                  <li><strong>Analytics cookies:</strong> Help us understand how you use our service (you can decline)</li>
                  <li><strong>Third-party cookies:</strong> Google OAuth, Polar for payments</li>
                </ul>
                <p className="leading-relaxed">
                  You can control cookie settings through your browser preferences or our cookie banner on first visit.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">9. Third-Party Services</h2>
              <div className="space-y-3">
                <p className="leading-relaxed">Our service integrates with third-party services:</p>
                <ul className="list-disc ml-6 space-y-1">
                  <li><strong>Google OAuth:</strong> For authentication (subject to Google's privacy policy)</li>
                  <li><strong>Polar:</strong> For payment processing (subject to Polar's privacy policy)</li>
                  <li><strong>Supabase:</strong> For database and authentication services</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">10. Children's Privacy</h2>
              <p className="leading-relaxed">
                Our service is not intended for children under 16 years of age. We do not knowingly collect personal information from children under 16. If you are a parent or guardian and believe your child has provided us with personal information, please contact us at support@simplestickers.app.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">11. International Data Transfers</h2>
              <p className="leading-relaxed">
                Your information may be transferred to and processed in countries other than your country of residence. We ensure appropriate safeguards are in place to protect your personal information in accordance with applicable data protection laws.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">12. Changes to This Policy</h2>
              <p className="leading-relaxed">
                We may update this privacy policy from time to time. We will notify you of any material changes by posting the new policy on this page and updating the "Last updated" date.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">13. Automated Decision-Making</h2>
              <p className="leading-relaxed">
                Our service uses AI algorithms to generate task suggestions. This feature is purely assistive and does not create decisions with legal or significant effects on users. You can choose not to use suggestions at any time.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">14. Supervisory Authority</h2>
              <div className="space-y-3">
                <p className="leading-relaxed">
                  You have the right to lodge a complaint with the supervisory authority for data protection:
                </p>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p><strong>Czech Office for Personal Data Protection (ÚOOÚ)</strong></p>
                  <p className="text-sm mt-2">Pplk. Sochora 27, 170 00 Prague 7</p>
                  <p className="text-sm">Web: <a href="https://www.uoou.cz" className="text-indigo-600 hover:underline">www.uoou.cz</a></p>
                  <p className="text-sm">Email: posta@uoou.cz</p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">15. How to Exercise Your Rights</h2>
              <div className="space-y-3">
                <p className="leading-relaxed">
                  To exercise your rights under GDPR (data access, rectification, erasure, portability, objection, etc.):
                </p>
                <ul className="list-disc ml-6 space-y-1">
                  <li>Send us a request at <strong>support@simplestickers.app</strong></li>
                  <li>We will respond within <strong>1 month</strong> of receiving your request</li>
                  <li>We may ask you to verify your identity</li>
                  <li>The service is <strong>free of charge</strong> (unless request is manifestly unfounded or excessive)</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">16. Contact Us</h2>
              <p className="leading-relaxed">
                If you have any questions about this privacy policy or our data practices, please contact us:
              </p>
              <div className="mt-4 bg-gray-50 p-4 rounded-lg">
                <p className="text-sm text-gray-700">
                  <strong>Email:</strong> support@simplestickers.app
                </p>
                <p className="text-xs text-gray-500 mt-2">
                  For all questions regarding privacy, data requests, support, and other inquiries
                </p>
              </div>
            </section>
          </div>
        </article>
      </div>
    </div>
  );
}